import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { LANGUAGE_OPTIONS } from "../../features/localization/languages";
import { changeLanguage } from "../../features/localization/localizationSlice";
import { PortalProfile } from "../../features/portal/models/PortalProfile";
import { fetchPortalProfile, updatePortalProfile } from "../../features/portal/slice-extensions/portal-me-slice-extension";
import { pickPortalLanguage, readPortalLanguageChoice } from "../../features/portal/utils/portalLanguage";

interface Props {
  memberId: string;
  /** The signed-in member's own details: a saved language also switches the portal (unless picked in the header). */
  isSelf: boolean;
}

/**
 * Your details (PD-0036): name and email read-only with the note that the school changes them, and the
 * language mail and documents arrive in, saved on change.
 */
const PortalProfileCard = ({ memberId, isSelf }: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "failed">("idle");

  useEffect(() => {
    let active = true;
    setProfile(null);
    setStatus("idle");
    dispatch(fetchPortalProfile(memberId))
      .unwrap()
      .then((loaded) => active && setProfile(loaded))
      .catch(() => active && setProfile(null));
    return () => {
      active = false;
    };
  }, [dispatch, memberId]);

  if (!profile) return null;

  const save = async (languageCode: string) => {
    setStatus("saving");
    try {
      const saved = await dispatch(
        updatePortalProfile({ memberId, request: { primaryLanguageCode: languageCode } }),
      ).unwrap();
      setProfile(saved);
      setStatus("saved");
      const target = isSelf ? pickPortalLanguage(saved.languageCode, i18n.language, readPortalLanguageChoice()) : null;
      if (target) dispatch(changeLanguage(target));
    } catch {
      setStatus("failed");
    }
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="text-base font-semibold text-grey-20 dark:text-white">{t("portal.me.profile")}</h2>
      <p className="mt-2 text-sm text-grey-20 dark:text-white">
        {`${profile.firstName} ${profile.lastName}`.trim()}
      </p>
      {profile.email && <p className="text-sm text-grey-40 dark:text-grey-80">{profile.email}</p>}
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">{t("portal.me.nameNote")}</p>
      <label htmlFor="portal-me-language" className="mt-4 block text-sm font-medium text-grey-20 dark:text-white">
        {t("portal.me.language")}
      </label>
      <div className="mt-1 flex items-center gap-3">
        <select
          id="portal-me-language"
          className="input w-auto"
          value={profile.languageCode}
          disabled={status === "saving"}
          onChange={(event) => save(event.target.value)}
        >
          {LANGUAGE_OPTIONS.map((language) => (
            <option key={language.code} value={language.code}>
              {language.label}
            </option>
          ))}
        </select>
        {status === "saved" && (
          <span role="status" className="text-sm text-green-700 dark:text-green-400">
            {t("portal.me.saved")}
          </span>
        )}
      </div>
      {status === "failed" && <p className="field-error mt-2">{t("portal.me.saveFailed")}</p>}
    </div>
  );
};

export default PortalProfileCard;
